import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";

const VALIDATION_PATTERNS = {
  email: "[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}",
  phone: "[\\+]?[(]?[0-9]{3}[)]?[-\\s\\.]?[0-9]{3}[-\\s\\.]?[0-9]{4,6}",
  url: "https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)",
  "numbers-only": "[0-9]*",
  "letters-only": "[a-zA-Z]*",
};

const KNOWN_VALIDATION_PATTERN_VALUES = new Set(Object.values(VALIDATION_PATTERNS));
type ValidationPatternName = keyof typeof VALIDATION_PATTERNS;

const isValidationPatternName = (value: string): value is ValidationPatternName =>
  Object.hasOwn(VALIDATION_PATTERNS, value);

export class InputCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Input type patterns
    this.commandPatterns.set(
      /(?:set |change )?(?:input )?type (?:to )?(text|email|password|number|tel|url|search|date|time|datetime-local|month|week|color|file)/i,
      (match) => ({ properties: { type: match[1].toLowerCase() } })
    );

    // Placeholder patterns
    this.commandPatterns.set(
      /(?:set |change )?placeholder (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { placeholder: match[1] } })
    );

    this.commandPatterns.set(
      /remove placeholder/i,
      () => ({ properties: { placeholder: "" } })
    );

    // Label patterns
    this.commandPatterns.set(
      /(?:add |set )?label (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { label: match[1] } })
    );

    // Value patterns
    this.commandPatterns.set(
      /(?:set |change )?(?:value|default) (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { value: match[1] } })
    );

    this.commandPatterns.set(
      /clear value/i,
      () => ({ properties: { value: "" } })
    );

    // Required pattern
    this.commandPatterns.set(
      /(?:make |set )?(required|optional)/i,
      (match) => ({ properties: { required: match[1].toLowerCase() === "required" } })
    );

    // Disabled/Readonly patterns
    this.commandPatterns.set(
      /(enable|disable)(?:d)?(?:\s+input)?/i,
      (match) => ({ properties: { disabled: match[1].toLowerCase() === "disable" } })
    );

    this.commandPatterns.set(
      /(?:make |set )?(readonly|editable)/i,
      (match) => ({ properties: { readOnly: match[1].toLowerCase() === "readonly" } })
    );

    // Validation patterns
    this.commandPatterns.set(
      /(?:set )?(?:min |minimum )(?:length )?(?:to )?(\d+)/i,
      (match) => ({ properties: { minLength: parseInt(match[1], 10) } })
    );

    this.commandPatterns.set(
      /(?:set )?(?:max |maximum )(?:length )?(?:to )?(\d+)/i,
      (match) => ({ properties: { maxLength: parseInt(match[1], 10) } })
    );

    this.commandPatterns.set(
      /(?:set )?pattern (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { pattern: match[1] } })
    );

    this.commandPatterns.set(
      /(?:set )?validation pattern (?:to )?(email|phone|url|numbers-only|letters-only)/i,
      (match) => {
        const patternName = match[1].toLowerCase();
        if (!isValidationPatternName(patternName)) {
          return null;
        }
        return { properties: { pattern: VALIDATION_PATTERNS[patternName] } };
      }
    );

    // For number inputs
    this.commandPatterns.set(
      /(?:set )?(?:min |minimum )(?:value )?(?:to )?(-?\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { min: parseFloat(match[1]) } })
    );

    this.commandPatterns.set(
      /(?:set )?(?:max |maximum )(?:value )?(?:to )?(-?\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { max: parseFloat(match[1]) } })
    );

    this.commandPatterns.set(
      /(?:set )?step (?:to )?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { step: parseFloat(match[1]) } })
    );

    // Autocomplete patterns
    this.commandPatterns.set(
      /(?:enable |disable )?autocomplete ?(on|off)?/i,
      (match) => ({ properties: { autoComplete: match[1] || "on" } })
    );

    // Size patterns
    this.commandPatterns.set(
      /(?:make |set )?(?:input )?(?:size )?(?:to )?(small|medium|large|sm|md|lg)/i,
      (match) => {
        const sizeMap: Record<string, string> = {
          small: "sm", sm: "sm",
          medium: "md", md: "md",
          large: "lg", lg: "lg"
        };
        const size = match[1].toLowerCase();
        return { properties: { size: sizeMap[size] || size } };
      }
    );

    // Full width pattern
    this.commandPatterns.set(
      /(?:make )?(?:input )?full[\s-]?width/i,
      () => ({ properties: { fullWidth: true } })
    );

    // Error state patterns
    this.commandPatterns.set(
      /(?:show |add )?error (?:message )?["']([^"']+)["']/i,
      (match) => ({ properties: { error: true, errorMessage: match[1] } })
    );

    this.commandPatterns.set(
      /(?:remove |clear )?error/i,
      () => ({ properties: { error: false, errorMessage: null } })
    );

    // Helper text patterns
    this.commandPatterns.set(
      /(?:add |set )?(?:helper |help )(?:text )?(?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { helperText: match[1] } })
    );

    // Icon patterns
    this.commandPatterns.set(
      /(?:add |set )?(?:start |left )?icon ["']?([a-zA-Z0-9-_]+)["']?/i,
      (match) => ({ properties: { startIcon: match[1] } })
    );

    this.commandPatterns.set(
      /(?:add |set )?(?:end |right )?icon ["']?([a-zA-Z0-9-_]+)["']?/i,
      (match) => ({ properties: { endIcon: match[1] } })
    );

    // Form field binding patterns
    this.commandPatterns.set(
      /(?:bind|map|connect) (?:to )?(?:field )?["']?([a-zA-Z0-9_]+)["']?/i,
      (match) => ({ properties: { fieldName: match[1] } })
    );

    this.commandPatterns.set(
      /(?:set )?field ?name (?:to )?["']?([a-zA-Z0-9_]+)["']?/i,
      (match) => ({ properties: { fieldName: match[1] } })
    );

    this.commandPatterns.set(
      /for (?:field |variable )?["']?([a-zA-Z0-9_]+)["']?/i,
      (match) => ({ properties: { fieldName: match[1] } })
    );

    // Remove field binding
    this.commandPatterns.set(
      /(?:remove |clear |unbind )(?:field|binding)/i,
      () => ({ properties: { fieldName: undefined } })
    );

    // Show/hide label patterns
    this.commandPatterns.set(
      /show label/i,
      () => ({ properties: { showLabel: true } })
    );

    this.commandPatterns.set(
      /hide label/i,
      () => ({ properties: { showLabel: false } })
    );

    this.commandPatterns.set(
      /(?:set )?label (?:text )?(?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { labelText: match[1], showLabel: true } })
    );
  }

  canHandle(command: string, component: Component): boolean {
    return component.type === ComponentType.INPUT && this.hasInputKeywords(command);
  }

  private hasInputKeywords(command: string): boolean {
    const inputKeywords = [
      "input", "field", "textbox", "textfield",
      "type", "email", "password", "number", "tel", "url", "search", "date",
      "placeholder", "label", "value", "default",
      "required", "optional", "disabled", "readonly", "editable",
      "validation", "validation pattern", "pattern", "min", "max", "length", "step",
      "autocomplete", "error", "helper", "hint",
      "icon", "clear",
      // Form field binding keywords
      "bind", "map", "connect", "fieldname", "field name", "variable",
      "show label", "hide label"
    ];
    const lowerCommand = command.toLowerCase();
    return inputKeywords.some(keyword => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    // Split complex commands
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {}, style: {} };

    for (const part of commandParts) {
      // Try pattern matching
      const patternMatch = this.matchPatterns(part, component);
      if (patternMatch) {
        if (patternMatch.properties) {
          changes.properties = { ...changes.properties, ...patternMatch.properties };
        }
        if (patternMatch.style) {
          changes.style = { ...changes.style, ...patternMatch.style };
        }
      }

      // Parse colors
      const colorValue = this.parseColorValue(part);
      if (colorValue) {
        const lowerPart = part.toLowerCase();
        if (lowerPart.includes("background") || lowerPart.includes("bg")) {
          changes.style = { ...changes.style, backgroundColor: colorValue };
        } else if (lowerPart.includes("border")) {
          changes.style = { ...changes.style, borderColor: colorValue };
        } else if (lowerPart.includes("text") || lowerPart.includes("color")) {
          changes.style = { ...changes.style, color: colorValue };
        }
      }

      // Parse size values - IMPORTANT: Use 'size' not 'style' for dimensions
      if (part.includes("width") && !part.includes("full")) {
        const widthValue = this.parseNumericValue(part);
        if (widthValue) {
          changes.size = { ...changes.size, width: widthValue };
        }
      }

      // Parse padding
      if (part.includes("padding")) {
        const paddingValue = this.parseNumericValue(part);
        if (paddingValue) {
          changes.style = { ...changes.style, padding: paddingValue };
        }
      }

      // Parse border radius
      if (part.includes("rounded") || part.includes("radius")) {
        const radiusMatch = part.match(/(\d+)(?:px)?/i);
        if (radiusMatch) {
          changes.style = { ...changes.style, borderRadius: `${radiusMatch[1]}px` };
        } else if (part.includes("full")) {
          changes.style = { ...changes.style, borderRadius: "9999px" };
        } else {
          changes.style = { ...changes.style, borderRadius: "4px" };
        }
      }
    }

    // Check if we found any changes
    if (Object.keys(changes.properties || {}).length > 0 ||
        Object.keys(changes.style || {}).length > 0) {

      const validation = this.validateChanges(changes, component);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error,
          suggestions: await this.getSuggestions(component)
        };
      }

      return {
        success: true,
        command: {
          type: this.determineCommandType(changes),
          changes,
          confidence: this.calculateConfidence(command, changes),
          description: this.generateDescription(changes, component)
        },
        suggestions: await this.getSuggestions(component)
      };
    }

    return {
      success: false,
      error: "Could not understand the input command",
      suggestions: await this.getSuggestions(component)
    };
  }

  async getExampleCommands(): Promise<string[]> {
    return [
      'Set placeholder to "Enter your email"',
      'Change type to email',
      'Set type to password',
      'Make input type number',
      'Add label "Username"',
      'Set value to "default text"',
      'Clear value',
      'Make required',
      'Make optional',
      'Disable input',
      'Enable input',
      'Make readonly',
      'Set min length to 5',
      'Set max length to 50',
      'Set pattern to "[A-Za-z]+"',
      'Set min value to 0',
      'Set max value to 100',
      'Set step to 5',
      'Enable autocomplete',
      'Make input large',
      'Make full width',
      'Show error "Invalid email"',
      'Add helper text "Enter a valid email address"',
      'Add start icon "email"',
      'Set placeholder to "Search..." and add end icon "search"'
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];

    // Placeholder suggestions
    if (!component.properties?.placeholder) {
      suggestions.push('Add placeholder text', 'Set placeholder to "Enter text"');
    } else {
      suggestions.push('Change placeholder text', 'Remove placeholder');
    }

    // Type suggestions
    const currentType = component.properties?.type || "text";
    if (currentType !== "email") {
      suggestions.push("Change type to email");
    }
    if (currentType !== "password") {
      suggestions.push("Set type to password");
    }
    if (currentType !== "number") {
      suggestions.push("Make number input");
    }

    // Label suggestions
    if (!component.properties?.label) {
      suggestions.push('Add input label');
    }

    // Validation suggestions
    if (!component.properties?.required) {
      suggestions.push("Make required");
    } else {
      suggestions.push("Make optional");
    }

    if (!component.properties?.minLength) {
      suggestions.push("Set minimum length");
    }
    if (!component.properties?.maxLength) {
      suggestions.push("Set maximum length");
    }

    // State suggestions
    if (component.properties?.disabled) {
      suggestions.push("Enable input");
    } else {
      suggestions.push("Disable input");
    }

    if (!component.properties?.readOnly) {
      suggestions.push("Make readonly");
    } else {
      suggestions.push("Make editable");
    }

    // Helper text suggestions
    if (!component.properties?.helperText) {
      suggestions.push("Add helper text");
    }

    // Size suggestions
    if (!component.properties?.fullWidth) {
      suggestions.push("Make full width");
    }

    const currentSize = component.properties?.size || "md";
    if (currentSize !== "lg") {
      suggestions.push("Make input large");
    }
    if (currentSize !== "sm") {
      suggestions.push("Make input small");
    }

    return suggestions.slice(0, 10);
  }

  private isSafeInputPattern(pattern: string): boolean {
    if (KNOWN_VALIDATION_PATTERN_VALUES.has(pattern)) {
      return true;
    }

    if (pattern.length === 0 || pattern.length > 80) {
      return false;
    }

    if (!/^[A-Za-z0-9\s._%+\-@:[\]\\/^$*?{}]+$/.test(pattern)) {
      return false;
    }

    if (/[()|]/.test(pattern)) {
      return false;
    }

    return !/\{\d{4,}|\{,\d{4,}|\{\d+,\d{4,}\}/.test(pattern);
  }

  protected override validateChanges(changes: PropertyChange, component: Component): { valid: boolean; error?: string } {
    // Validate input type
    if (changes.properties?.type) {
      const validTypes = [
        "text", "email", "password", "number", "tel", "url", "search",
        "date", "time", "datetime-local", "month", "week", "color", "file"
      ];
      if (!validTypes.includes(changes.properties.type)) {
        return { valid: false, error: `Invalid input type: ${changes.properties.type}` };
      }
    }

    // Validate size
    if (changes.properties?.size) {
      const validSizes = ["sm", "md", "lg"];
      if (!validSizes.includes(changes.properties.size)) {
        return { valid: false, error: `Invalid size: ${changes.properties.size}` };
      }
    }

    // Validate autocomplete
    if (changes.properties?.autoComplete) {
      const validValues = ["on", "off"];
      if (!validValues.includes(changes.properties.autoComplete)) {
        return { valid: false, error: `Invalid autocomplete value: ${changes.properties.autoComplete}` };
      }
    }

    // Validate min/max for number inputs
    if ((changes.properties?.min !== undefined || changes.properties?.max !== undefined) &&
        component.properties?.type !== "number" &&
        changes.properties?.type !== "number") {
      return { valid: false, error: "Min/max values can only be set for number inputs" };
    }

    // Validate min < max
    if (changes.properties?.min !== undefined && changes.properties?.max !== undefined) {
      if (changes.properties.min > changes.properties.max) {
        return { valid: false, error: "Minimum value cannot be greater than maximum value" };
      }
    }

    const pattern = changes.properties?.pattern;
    if (pattern !== undefined) {
      if (typeof pattern !== "string" || !this.isSafeInputPattern(pattern)) {
        return { valid: false, error: "Invalid pattern: use a named validation pattern or a simple character-class pattern" };
      }
    }

    return super.validateChanges(changes, component);
  }
}

export const inputCommandHandler = new InputCommandHandler();
